import { useCallback, useEffect, useRef, useState } from 'react';
import { useCurrentUser } from 'hooks/useCurrentUser/suspense';
import useAuth from 'hooks/useAuth';
import { logger } from 'modules/logger';
import { InactivitySnackbar } from 'common/InactivityTracking';
import { telemetryContextFor } from 'modules/Apollo/types';

export const SessionExpirationNoticeSnackbar = ({
  message = 'Your session will automatically expire in %s.\nPlease complete your work.',
  sessionEndTime,
  paused,
}: {
  message?: string;
  sessionEndTime: number;
  paused?: boolean;
}) => {
  return (
    <InactivitySnackbar
      message={message}
      sessionEndTime={sessionEndTime}
      paused={paused}
      dataTestId="session-expiration-snackbar"
    />
  );
};

const SESSION_EXPIRATION_CHECK_INTERVAL = 1000 * 60; // 1 minute
export const MIN_TIME_FOR_NOTICE_MS = 5 * 1000 * 60; // 5 minutes
interface SessionExpirationNoticeProps {
  message?: string;
  /** 'warn' will only display the notice and take no action; if set to 'warn', this notice will no
   * longer appear after the initial expiration. */
  expireBehavior?: 'logout' | 'warn';
  /** Callback hook for when the session expires */
  onExpire?: () => void;
}
export const SessionExpirationNotice = ({
  message,
  expireBehavior = 'logout',
  onExpire,
}: SessionExpirationNoticeProps): React.ReactElement => {
  const { logout } = useAuth({ context: APOLLO_QUERY_CONTEXT });
  const data = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const sessionEndTimeRef = useRef<number>();
  const [displayNotice, setDisplayNotice] = useState<boolean>(false);
  const interval = useRef<NodeJS.Timeout>();

  const clearExpirationInterval = useCallback(() => {
    interval.current && clearInterval(interval.current);
  }, []);

  const checkSessionEndTime = useCallback(() => {
    // @ts-expect-error [EN-10263] - TS18048 - 'sessionEndTimeRef.current' is possibly 'undefined'.
    const timeRemaining = sessionEndTimeRef.current - Date.now();
    // Check if we should show the notice
    setDisplayNotice(timeRemaining <= MIN_TIME_FOR_NOTICE_MS);

    if (timeRemaining <= 0) {
      logger.info('[SessionExpirationNotice] Session expired');
      onExpire && onExpire();
      clearExpirationInterval();
      if (expireBehavior === 'logout') {
        logger.info('[SessionExpirationNotice] Logging out');
        logout();
      }
    }
  }, [clearExpirationInterval, expireBehavior, logout, onExpire]);

  const setupExpirationInterval = useCallback(() => {
    clearExpirationInterval();
    interval.current = setInterval(() => {
      checkSessionEndTime();
    }, SESSION_EXPIRATION_CHECK_INTERVAL);
  }, [checkSessionEndTime, clearExpirationInterval]);

  useEffect(() => {
    // @ts-expect-error Temporary suppression until other PR is merged
    const sessionExpiry = data?.me?.sessionExpirationTime;
    if (!sessionExpiry) {
      setDisplayNotice(false);
      clearExpirationInterval();
      // Do nothing if not configured
      return;
    }
    sessionEndTimeRef.current = sessionExpiry;
    setupExpirationInterval();

    return clearExpirationInterval;
  }, [data, setupExpirationInterval, clearExpirationInterval]);

  return (
    <SessionExpirationNoticeSnackbar
      message={message}
      // @ts-expect-error [EN-10263] - TS2322 - Type 'number | null | undefined' is not assignable to type 'number'.
      sessionEndTime={displayNotice ? sessionEndTimeRef.current : null}
    />
  );
};

SessionExpirationNotice.displayName = 'SessionExpirationNotice';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(SessionExpirationNotice);
